/**
 * Dialog for editing a Storefront Activity Rule (data-for-points, plan 1904).
 * Editable: points, promo message, window. The Page/Section/Action triple is
 * READ-ONLY — changing the activity type would orphan already-awarded ledger
 * entries, so the admin must delete + recreate instead (UX decision 4).
 */

import { useEffect, useState } from "react";
import { Loader2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useUpdateActivity,
  useActivityRegistry,
  type ActivityRule,
} from "../api/activitiesAdminHooks";

interface EditActivityDialogProps {
  rule: ActivityRule | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditActivityDialog({
  rule,
  open,
  onOpenChange,
}: EditActivityDialogProps) {
  const [points, setPoints] = useState(3);
  const [oneTimeUse, setOneTimeUse] = useState(true);
  const [maxPoints, setMaxPoints] = useState(0);
  const [promo, setPromo] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const updateMutation = useUpdateActivity();
  const registryQuery = useActivityRegistry(open);

  // Re-populate whenever the dialog opens for a (possibly different) rule.
  useEffect(() => {
    if (rule) {
      setPoints(rule.points);
      setOneTimeUse(rule.one_time_use !== 0);
      setMaxPoints(rule.max_points_per_customer ?? 0);
      setPromo(rule.promo_message ?? "");
      setValidFrom(rule.valid_from ?? "");
      setValidTo(rule.valid_to ?? "");
      setAdvancedOpen(!!(rule.valid_from || rule.valid_to));
    }
  }, [rule]);

  // Epic 09: repeatable only if the activity has a per-occurrence reference.
  const referenceSource =
    registryQuery.data?.pages
      .find((p) => p.key === rule?.page)
      ?.sections.find((s) => s.key === rule?.section)
      ?.actions.find((a) => a.key === rule?.action)?.reference_source ?? null;
  const canRepeat = !!referenceSource;
  const effectiveOneTime = canRepeat ? oneTimeUse : true;

  if (!rule) return null;

  const typeLabel = [
    rule.labels?.page_label ?? rule.page,
    rule.labels?.section_label ?? rule.section,
    rule.labels?.action_label ?? rule.action,
  ].join(" › ");

  const handleSubmit = () => {
    if (points <= 0) {
      toast.error("แต้มต้องมากกว่า 0");
      return;
    }
    if (validFrom && validTo && validTo < validFrom) {
      toast.error("วันสิ้นสุดต้องไม่ก่อนวันเริ่ม");
      return;
    }
    if (maxPoints > 0 && maxPoints < points) {
      toast.error("เพดานคะแนนรวมต้องไม่น้อยกว่าแต้มต่อครั้ง");
      return;
    }
    updateMutation.mutate(
      {
        name: rule.name,
        points,
        one_time_use: effectiveOneTime ? 1 : 0,
        max_points_per_customer: maxPoints,
        valid_from: validFrom || null,
        valid_to: validTo || null,
        promo_message: promo.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success("บันทึกสำเร็จ");
          onOpenChange(false);
        },
        onError: (err: Error) => {
          toast.error(err.message || "เกิดข้อผิดพลาด");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>แก้ไขกิจกรรม</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              ประเภทกิจกรรม (ไม่สามารถเปลี่ยนได้)
            </p>
            <div className="rounded-md bg-muted/50 p-3 text-sm font-medium">
              {typeLabel}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-activity-points">แต้มที่จะได้รับ</Label>
            <div className="flex items-center gap-2">
              <Input
                id="edit-activity-points"
                type="number"
                min={1}
                step={1}
                value={points}
                onChange={(e) => setPoints(Number(e.target.value))}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">แต้ม</span>
            </div>
          </div>

          <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="edit-activity-onetime">ให้ครั้งเดียว</Label>
              <p className="text-xs text-muted-foreground">
                {canRepeat
                  ? "ปิด = ทำซ้ำได้ ได้แต้มทุกครั้งที่ทำใหม่ (เช่น รีวิวต่อสินค้า)"
                  : "กิจกรรมนี้ทำซ้ำไม่ได้โดยธรรมชาติ"}
              </p>
            </div>
            <Switch
              id="edit-activity-onetime"
              checked={effectiveOneTime}
              disabled={!canRepeat}
              onCheckedChange={setOneTimeUse}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-activity-maxpoints">
              เพดานคะแนนรวมต่อลูกค้า{" "}
              <span className="font-normal text-muted-foreground">(0 = ไม่จำกัด)</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="edit-activity-maxpoints"
                type="number"
                min={0}
                step={1}
                value={maxPoints}
                onChange={(e) => setMaxPoints(Number(e.target.value))}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">แต้ม</span>
            </div>
            <p className="text-xs text-muted-foreground">
              ถึงเพดานแล้วยังทำกิจกรรมได้ แต่ไม่ได้แต้มเพิ่ม
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-activity-promo">
              ข้อความโปรโมชัน{" "}
              <span className="font-normal text-muted-foreground">(ไม่จำเป็น)</span>
            </Label>
            <Input
              id="edit-activity-promo"
              value={promo}
              maxLength={100}
              onChange={(e) => setPromo(e.target.value)}
              placeholder="กรอกวันเกิด รับ 3 คะแนน"
            />
          </div>

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-sm text-muted-foreground">
              <ChevronRight
                className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-90" : ""}`}
              />
              ขั้นสูง
            </CollapsibleTrigger>
            <CollapsibleContent className="grid grid-cols-2 gap-3 pt-3">
              <div className="space-y-2">
                <Label htmlFor="edit-activity-from">วันเริ่มต้น</Label>
                <Input
                  id="edit-activity-from"
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-activity-to">วันสิ้นสุด</Label>
                <Input
                  id="edit-activity-to"
                  type="date"
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                />
              </div>
              <p className="col-span-2 text-xs text-muted-foreground">
                ไม่ระบุ = ใช้งานได้ตลอด
              </p>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            บันทึก
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
